export type PageEntity = { entityType?: string; entityId?: number; canonicalRoute?: string; label?: string };

// SECURITY: the result is injected into the *trusted* system-reminder channel, which bypasses
// the untrusted-page-context tool lockdown — so anything that reaches here is treated as fully
// trusted by the agent. `window.location.hash` is attacker-influenceable via a crafted link
// (extra path segments after a matched prefix, e.g. `/#/accounts/123/<injected prose>`).
//
// We therefore NEVER echo the raw route. Everything emitted is a CONSTANT from this module: a
// fixed `label`, plus — for entity pages — a `canonicalRoute` rebuilt from the matched template
// and the parsed numeric id. So the output can only ever contain our own fixed strings and
// digits, regardless of what the URL carried. Extend the tables with new patterns; never widen
// them to pass URL text through.

// Entity pages: a primary record the agent can look up with its read tools.
const ENTITY_ROUTES: { re: RegExp; entityType: string; canonical: (id: number) => string }[] = [
	{ re: /^\/accounts\/(\d+)/, entityType: 'company', canonical: id => `/accounts/${id}` }
];

// Section pages with no single primary record — emit a fixed human label so the agent knows
// roughly where the user is. Order matters: first match wins.
const SECTION_ROUTES: { re: RegExp; label: string }[] = [
	{ re: /^\/salesboard/, label: 'the Sales board (pipeline overview)' },
	{ re: /^\/(?:orders|opportunities)/, label: 'the Orders / opportunities list' },
	{ re: /^\/companies/, label: 'the Companies list' },
	{ re: /^\/contacts/, label: 'the Contacts list' },
	{ re: /^\/campaigns/, label: 'the Campaigns view' },
	{ re: /^\/flows/, label: 'the Flows view' },
	{ re: /^\/forms/, label: 'the Forms view' },
	{ re: /^\/prospecting/, label: 'Find new prospects (prospecting)' },
	{ re: /^\/ai-workspace/, label: 'the AI Workspace' },
	{ re: /^\/reports?/, label: 'Reports' },
	{ re: /^\/dashboard/, label: 'the Dashboard' }
];

export function routeToEntity(hash: string): PageEntity {
	// Strip the `#`, query string, and any legacy `/<customerId>/` prefix some hash URLs carry
	// (e.g. `/#/456/accounts/123/`) up front, so the route tables match a normalised path.
	const route = hash
		.replace(/^#/, '')
		.split('?')[0]
		.replace(/^\/\d+\//, '/');
	for (const { re, entityType, canonical } of ENTITY_ROUTES) {
		const m = route.match(re);
		if (m) {
			const entityId = Number(m[1]);
			return { entityType, entityId, canonicalRoute: canonical(entityId) };
		}
	}
	for (const { re, label } of SECTION_ROUTES) {
		if (re.test(route)) return { label };
	}
	return {};
}

export function buildTrustedContextString(entity: PageEntity): string | null {
	if (entity.entityType && entity.entityId && entity.canonicalRoute) {
		return [
			'The user is currently viewing this page in the Upsales CRM (trusted context).',
			`Route: ${entity.canonicalRoute}`,
			`Primary record: ${entity.entityType} id ${entity.entityId}. Use your read tools to look it up if relevant.`
		].join('\n');
	}
	if (entity.label) {
		return `The user is currently viewing ${entity.label} in the Upsales CRM (trusted context).`;
	}
	// Unrecognised route — no structural data to share, and we must not echo the raw route.
	return null;
}
